import * as THREE from 'three';

import {
  pitchMap,
  isClockWise,
  dist,
  counterClockWise,
  calculateRoll,
  expandPolygon,
  expandPolygonWithRoll,
  calculatePolygonZdim,
  pointInConvexPolygon,
  pointInConcavePolygon,
  polygonContainsPolygon,
  calculateRoofHeight,
  optimizeVertexPosition,
  getEaveIndices,
  getEave,
  shrinkPolygon,
  borderPolygon,
  getParentsPolygon,
  segmentProperties,
  projectToLine,
  protectMethod,
  getRotatedRect,
  polygonsIntersect,
  computeZOnPlane,
  getPlaneAxisForPanels,
  getPlaneNormal,
  isPointInPoly,
  mapPanelsTo3D
} from '../algorithms/geometry.js';

const debugPanelPlacement = (...args) => {
  if (typeof window === 'undefined' || window.__debugPanelPlacement !== true) return;
  console.log('[runPanelPlacement]', ...args);
};

const getSegmentRange = (orgVertices, eaves, scale, index) => {
  // const cw = changeClockwise[index];
  const z = new THREE.Vector3(0, 0, 1);
  const geo = orgVertices[index];
  const [ea, eb, et, ai, bi] = eaves[index];
  let a;
  let b;
  if (ai || bi) {
    a = geo[ai % geo.length]; // new THREE.Vector3(geo[ai][0], geo[ai][1], 0);
    b = geo[bi % geo.length]; // new THREE.Vector3(geo[bi][0], geo[bi][1], 0);
  } else if (et === 'eave') {
    a = ea;
    b = eb;
  } else {
    a = eb;
    b = ea;
  }
  const xdir = b.clone().sub(a);
  const ydir = xdir.clone().cross(z);
  const e = a.clone().add(ydir);
  xdir.normalize();
  ydir.normalize();
  let [minx, maxx, miny, maxy] = [0, 0, 0, 0];
  geo.forEach((p) => {
    // const p = new THREE.Vector3(x, y, 0);
    const [p1] = projectToLine([a, b], p);
    const l1 = p1.clone().sub(a).length();
    if (p1.clone().sub(a).dot(xdir) > 0) maxx = Math.max(l1, maxx);
    else minx = Math.max(l1, minx);
    const [p2] = projectToLine([a, e], p);
    const l2 = p2.clone().sub(a).length();
    if (p2.clone().sub(a).dot(ydir) < 0) maxy = Math.max(l2, maxy);
    else miny = Math.max(l2, miny);
  });
  return [minx / scale, maxx / scale, maxy / scale, miny / scale];
};

const cartesian = (...a) => a.reduce((i, b) => i.flatMap((d) => b.map((e) => [d, e].flat())));

export const getRoofObstacles = (
  vertices,
  orgVertices,
  verticesType,
  parents,
  index,
  getSetbacks,
  minObstacleSetback = 0
) => {
  const obstacleIndices = vertices
    .map((_, oi) => {
      if (Array.isArray(verticesType) && verticesType[oi] !== 'obstacle') return [oi, false];
      const obstacleParents = parents?.[oi] ?? getParentsPolygon(orgVertices, oi, false, verticesType);
      const overlapsRoof = verticesType?.[oi] === 'obstacle' && polygonsIntersect(vertices[oi], vertices[index]);
      return [oi, obstacleParents.includes(index) || overlapsRoof];
    })
    .filter(([, isChild]) => isChild)
    .map(([oi]) => oi);
  if (obstacleIndices.length) {
    const regions = obstacleIndices
      .map((oi) => {
        const poly = vertices[oi];
        if (!Array.isArray(poly) || poly.length < 2) return [];
        const setback = getSetbacks(poly, oi);
        if (Array.isArray(setback)) return [];
        const setbackDistance = setback == 0 && minObstacleSetback > 0 ? minObstacleSetback : Math.abs(setback);
        if (!setbackDistance) return [];
        return borderPolygon(poly, Math.min(setbackDistance, 1), setbackDistance, poly.length >= 3 && setback >= 0);
      })
      .flat();
    const obstacle = obstacleIndices
      .map((oi) => {
        const poly = vertices[oi];
        if (!Array.isArray(poly) || poly.length < 3) return null;
        return poly;
      })
      .filter((o) => o != null);
    return [...obstacle, ...regions];
  }
  return [];
};

export const intersectsExcludedPolygons = (poly, excludedPolygons) =>
  excludedPolygons.some((excludedPolygon) => polygonsIntersect(poly, excludedPolygon, false, 1e-5));

export const initializePanel = (s, pitch, tilt, panelType, resolution) => {
  const safeSegment = s ?? {};
  const safePanelType = panelType ?? {};
  const safeResolution = Number.parseFloat(resolution) || 1;
  const ph = Number.parseFloat(safePanelType.length ?? 0) || 0;
  const pw = Number.parseFloat(safePanelType.width ?? 0) || 0;
  const panelLength = ph / safeResolution / 10;
  const panelWidth = pw / safeResolution / 10;
  const pitchValue = Number.parseFloat(pitch ?? 0) || 0;
  const tiltValue = Number.parseFloat(tilt ?? 0) || 0;
  const theta = pitchValue + tiltValue;
  const panelHScale = theta ? Math.sqrt(1 + Math.tan(theta * (Math.PI / 180.0)) ** 2) : 1;
  return {
    id: safeSegment.id ?? null,
    azimuth: Number.parseFloat(safeSegment.azimuth ?? 0) || 0,
    panelAxis: safeSegment.panelAxis ?? null,
    tilt: tiltValue,
    horizontal: {
      points: [],
      height: panelWidth, // / panelHScale,
      width: panelLength,
      selected: []
    },
    vertical: {
      points: [],
      height: panelLength, // / panelHScale,
      width: panelWidth,
      selected: []
    }
  };
};

export const initializePanels = (segments, pitches, tilt, panelType, resolution) =>
  segments.map((s, si) => initializePanel(s, pitches[si], tilt, panelType, resolution));

export const getVirtualPanels_ = (panelData) => {
  panelData.forEach((panel) => {
    const virtualPoints = [];
    const azimuthRad = ((panel.azimuth ?? 0) * Math.PI) / 180;
    const dirMain = { x: Math.cos(azimuthRad), y: Math.sin(azimuthRad) };
    const dirPerp = { x: -Math.sin(azimuthRad), y: Math.cos(azimuthRad) };
    const directions = [dirMain, { x: -dirMain.x, y: -dirMain.y }, dirPerp, { x: -dirPerp.x, y: -dirPerp.y }];

    const allRectangles = [];
    panel.points.forEach((pt) => {
      const [x0, y0, h, w, t] = pt;
      const rect = getRotatedRect(x0, y0, h, w, azimuthRad);
      allRectangles.push(rect);
      // for (let ix = 0; ix < 4; ix += 1)
      //   allRectangles.push([rect[ix].x, rect[ix].y, h, w, t]);
    });

    panel.points.forEach((pt) => {
      const [x0, y0, h, w, t] = pt;
      directions.forEach((dir, i) => {
        const distance = (i < 2 ? w : h) + 0.1;
        const newX = x0 + dir.x * distance;
        const newY = y0 + dir.y * distance;
        const newRect = getRotatedRect(newX, newY, h, w, azimuthRad);
        const exists = allRectangles.some((rect) => polygonsIntersect(rect, newRect));
        if (!exists) {
          virtualPoints.push([newX, newY, h, w, t]);
          allRectangles.push(newRect);
          // for (let ix = 0; ix < 4; ix += 1)
          //   allRectangles.push([newRect[ix].x, newRect[ix].y, h, w, t]);
        }
      });
    });
    // panel.boxes = allRectangles;
    panel.virtual_points = virtualPoints;
  });
};

export const getVirtualPanels = (panels, roof_vertices, obstacles_vertices, rackBuffer) => {
  const virtualPoints = [];
  const rects = panels.points3D.map((pnts) => pnts.map((p) => p.clone()));
  rects.push(...obstacles_vertices);
  panels.points3D.forEach((pnts, pnli) => {
    const [x0, y0, h, w, t] = panels.points[pnli];
    const [p1, p2, p3, p4] = pnts;
    const v_left = p2.clone().sub(p1);
    const v_top = p4.clone().sub(p1);
    const x_step = v_left.length() + rackBuffer;
    const y_step = v_top.length() + rackBuffer;
    const vx = v_left.clone().normalize().multiplyScalar(x_step);
    const vy = v_top.clone().normalize().multiplyScalar(y_step);
    const p_right = p1.clone().sub(vx);
    const p_top = p1.clone().add(vy);
    const p_left = p1.clone().add(vx);
    const p_bottom = p1.clone().sub(vy);
    [p_right, p_top, p_left, p_bottom].forEach((np1) => {
      const np2 = np1.clone().add(v_left);
      const np3 = np2.clone().add(v_top);
      const np4 = np1.clone().add(v_top);
      const newRect = [np1, np2, np3, np4];
      const collision_exists = rects.some((rect) => polygonsIntersect(rect, newRect, false, 0.01));
      const within_roof = polygonContainsPolygon(roof_vertices, newRect);
      if (!collision_exists && within_roof) {
        virtualPoints.push([np1.x, np1.y, h, w, t]);
        rects.push(newRect);
      }
    });
  });
  return [virtualPoints, rects];
};

export const getAxisVectors = (v) => {
  const arbitrary = new THREE.Vector3(1, 0, 0);
  if (Math.abs(v.dot(arbitrary)) > 0.99) {
    arbitrary.set(0, 1, 0);
  }

  const dir = v.clone().normalize();

  const u1 = new THREE.Vector3().crossVectors(dir, arbitrary).normalize();

  const u2 = new THREE.Vector3().crossVectors(u1, dir).normalize();

  const up = Math.abs(u1.z) > Math.abs(u2.z) ? u1 : u2;
  if (up.z < 0) up.negate();

  const perp = dir.cross(up).normalize();
  return [perp, up];
};

export const getPanelData = (
  panels,
  segments,
  pitches,
  vertices,
  parents,
  eaves,
  renderEaves,
  eaveHeights,
  cx,
  cy,
  scale,
  resolution,
  buffer,
  setbacksData
) => {
  const c = 1.0;
  const safePitches = Array.isArray(pitches) ? pitches : [];
  const safeEaves = Array.isArray(eaves) ? eaves : [];
  const safeRenderEaves = Array.isArray(renderEaves) ? renderEaves : safeEaves;
  const safeSetbacksData = Array.isArray(setbacksData) ? setbacksData : [];
  const safeParents = Array.isArray(parents) ? parents : [];
  const getPoint = (x, y) => [(x - cx / c) * scale, (y - cy / c) * scale];
  const projectPanel = (pi, pts) => {
    if (!(pi in safeRenderEaves && pi in safePitches)) return [];
    const eave = safeRenderEaves?.[pi];
    const pitch = safePitches?.[pi];
    if (!Array.isArray(eave) || !Number.isFinite(Number(pitch))) return [];
    const projectedPnts = expandPolygon(
      pts.map((v) => new THREE.Vector3(v[0], v[1], 0)),
      eave,
      pitch
    );
    return pts.map((v, vi) => [projectedPnts[vi].x, projectedPnts[vi].y, v[2], v[3], v[4]]);
  };

  const getPoints = (p) => {
    try {
      const hpts = Array.isArray(p?.horizontal?.points) ? p.horizontal.points : [];
      const vpts = Array.isArray(p?.vertical?.points) ? p.vertical.points : [];
      const hh = Number(p?.horizontal?.height) || 0;
      const hw = Number(p?.horizontal?.width) || 0;
      const vh = Number(p?.vertical?.height) || 0;
      const vw = Number(p?.vertical?.width) || 0;

      const horiz = hpts
        .filter((pt) => Array.isArray(pt) && pt.length >= 2)
        .map(([x, y]) => [...getPoint(x, y), hh * scale, hw * scale, 'h']);
      const vert = vpts
        .filter((pt) => Array.isArray(pt) && pt.length >= 2)
        .map(([x, y]) => [...getPoint(x, y), vh * scale, vw * scale, 'v']);

      return horiz.concat(vert);
    } catch (err) {
      return [];
    }
  };

  const getSamples = (p, pi) => {
    return [
      [0, 0, p.horizontal.height * scale, p.horizontal.width * scale, 'h'],
      [0, 0, p.vertical.height * scale, p.vertical.width * scale, 'v']
    ];
  };

  const countPanelCornersOutside = (pi, pts, azimuthShift) => {
    if (!Array.isArray(pts) || pts.length === 0) return 0;
    if (!eaves?.[pi] || !vertices?.[pi]) return Number.POSITIVE_INFINITY;
    const boxes = mapPanelsTo3D(eaves[pi], vertices[pi], pts, azimuthShift);
    let outside = 0;
    boxes.forEach((box) => {
      if (!Array.isArray(box)) return;
      box.forEach((corner) => {
        if (!isPointInPoly(corner, vertices[pi], true, 0.001)) outside += 1;
      });
    });
    return outside;
  };

  const resolveLegacyPlacement = (pi, pl) => {
    if (!pl || !Array.isArray(pl.points) || pl.points.length === 0) return false;
    if (!eaves?.[pi] || !vertices?.[pi]) return false;
    const [xdir, ydir] = getPlaneAxisForPanels(eaves[pi], vertices[pi], pl.azimuthShift);
    if (!xdir || !ydir) return false;
    const legacyPoints = pl.points.map(([vx, vy, vh, vw, t]) => [
      vx + xdir.x * vw - ydir.x * vh,
      vy + xdir.y * vw - ydir.y * vh,
      vh,
      vw,
      t
    ]);
    const brScore = countPanelCornersOutside(pi, pl.points, pl.azimuthShift);
    const tlScore = countPanelCornersOutside(pi, legacyPoints, pl.azimuthShift);
    if (!Number.isFinite(brScore) || !Number.isFinite(tlScore)) return false;
    if (tlScore === brScore) return false;
    return tlScore < brScore;
  };

  const newPanels = panels
    .map((p, pi) => {
      const tilt = p.tilt ?? 0;
      const theta = (safePitches?.[pi] ?? 0) + tilt;
      const panelData = {
        id: p.id,
        azimuth: p.azimuth ?? 0,
        azimuthShift: (segments[pi]?.azimuth ?? 0) - (p.azimuth ?? 0),
        tilt: tilt,
        samples: getSamples(p, pi),
        scale: theta ? Math.sqrt(1 + Math.tan(theta * (Math.PI / 180.0)) ** 2) : 1,
        points: getPoints(p),
        projectedPoints: [],
        points3D: []
      };
      return panelData;
    })
    .filter((p, pi) => pi < segments.length);

  const rackBuffer = mmToSceneUnit(buffer, resolution, scale);

  newPanels.forEach((pl, pli) => {
    let isLegacyFlag = segments?.[pli]?.is_lagacy;
    if (isLegacyFlag === null || isLegacyFlag === undefined) {
      const computedLegacy = resolveLegacyPlacement(pli, pl);
      if (segments?.[pli]) segments[pli].is_lagacy = computedLegacy;
      isLegacyFlag = computedLegacy;
    }
    const isLegacy = isLegacyFlag === true;
    if (isLegacy && eaves[pli] && vertices[pli]) {
      const [xdir, ydir] = getPlaneAxisForPanels(eaves[pli], vertices[pli], pl.azimuthShift);
      if (xdir && ydir) {
        pl.points = pl.points.map(([vx, vy, vh, vw, t]) => [
          vx + xdir.x * vw - ydir.x * vh,
          vy + xdir.y * vw - ydir.y * vh,
          vh,
          vw,
          t
        ]);
      }
    }

    pl.projectedPoints = projectPanel(pli, pl.points);
    pl.points3D = mapPanelsTo3D(eaves[pli], vertices[pli], pl.points, pl.azimuthShift);
    pl.samples3D = mapPanelsTo3D(eaves[pli], vertices[pli], pl.samples, pl.azimuthShift);
    // NOTE: "Line" obstacles (keepout polylines) come through as 2-point polylines.
    // They should not be treated as polygon obstacles for intersection tests here,
    const obstacles = vertices.slice(segments.length).filter((poly) => Array.isArray(poly) && poly.length >= 3);
    const setbacks = [safeSetbacksData[pli], safeSetbacksData.filter((_, sdi) => safeParents[sdi] == pli).flat()]
      .flat()
      .filter((rect) => Array.isArray(rect) && rect.length >= 3);

    pl.is_valid = pl.points3D.map((box) => {
      const intersectWithObstacle = obstacles.some((rect) => polygonsIntersect(rect, box));
      const intersectWithRoof = !polygonContainsPolygon(vertices[pli], box, 0.001);
      if (intersectWithObstacle || intersectWithRoof) return 2;
      const intersectWithSetback = setbacks.some((rect) => polygonsIntersect(rect, box));
      if (intersectWithSetback) return 1;
      return 0;
    });

    const [virtual_points, rects] = getVirtualPanels(pl, vertices[pli], obstacles, rackBuffer);
    const virtual_projectedPoints = projectPanel(pli, virtual_points);
    const virtual_points3D = mapPanelsTo3D(eaves[pli], vertices[pli], virtual_points, pl.azimuthShift);
    const lenPoints = pl.points3D.length;
    const real_panel_rects = pl.points3D.map((pnts) => pnts.map((p) => p.clone()));

    pl.points = pl.points.concat(virtual_points);
    pl.projectedPoints = pl.projectedPoints.concat(virtual_projectedPoints);
    pl.points3D = pl.points3D.concat(virtual_points3D);
    pl.realPanels = Array.from({ length: pl.points3D.length }, (_, idx) => idx < lenPoints);
    pl.virtual_points3D = rects.flat();
    pl.snap_points = real_panel_rects;
    pl.is_valid = pl.is_valid.concat(Array.from({ length: virtual_points3D.length }, () => true));
  });

  return newPanels;
};

const mmToSceneUnit = (mm, resolution, scale = 1) => Math.max(0.001, mm / 10 / Number(resolution)) * scale || 0.001;

const getLayoutClusterSizes = (verticals, horizontals, panelVW, panelVH, panelHW, panelHH, dirn, ydir, rackBuffer) => {
  const gap = Math.max(rackBuffer, 0.001);
  const tolerance = Math.max(gap * 0.1, 0.001);
  const items = [
    ...verticals.map((point) => ({ point, width: panelVW, height: panelVH })),
    ...horizontals.map((point) => ({ point, width: panelHW, height: panelHH }))
  ].map((item) => {
    const x = item.point[0] * dirn.x + item.point[1] * dirn.y;
    const y = item.point[0] * ydir.x + item.point[1] * ydir.y;
    return {
      minX: x - item.width,
      maxX: x,
      minY: y,
      maxY: y + item.height
    };
  });
  const getBoxesGap = (a, b) => ({
    x: Math.max(a.minX - b.maxX, b.minX - a.maxX, 0),
    y: Math.max(a.minY - b.maxY, b.minY - a.maxY, 0)
  });
  const areConnected = (a, b) => {
    const boxGap = getBoxesGap(a, b);
    return (boxGap.x <= gap + tolerance && boxGap.y <= tolerance) || (boxGap.y <= gap + tolerance && boxGap.x <= tolerance);
  };
  const sizes = [];
  const visited = new Set();
  items.forEach((item, itemIndex) => {
    if (visited.has(itemIndex)) return;
    let size = 0;
    const queue = [itemIndex];
    visited.add(itemIndex);
    while (queue.length) {
      const currentIndex = queue.shift();
      size += 1;
      items.forEach((candidate, candidateIndex) => {
        if (visited.has(candidateIndex) || !areConnected(items[currentIndex], candidate)) return;
        visited.add(candidateIndex);
        queue.push(candidateIndex);
      });
    }
    sizes.push(size);
  });
  return sizes.sort((a, b) => b - a);
};

const compareClusterSizes = (a, b) => {
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i += 1) {
    const av = a[i] ?? 0;
    const bv = b[i] ?? 0;
    if (av !== bv) return av - bv;
  }
  return 0;
};

const fineTunePanelClusters = (
  panelData,
  dirn,
  ydir,
  scale,
  center,
  testPoly,
  excludedPolygons,
  roofPolygon,
  rackBuffer
) => {
  const [cx, cy] = center;
  const gap = Math.max(rackBuffer, 0.001);
  const tolerance = Math.max(gap * 0.1, 0.001);
  const items = [
    ...panelData.vertical.points.map((point) => ({
      point,
      width: panelData.vertical.width,
      height: panelData.vertical.height
    })),
    ...panelData.horizontal.points.map((point) => ({
      point,
      width: panelData.horizontal.width,
      height: panelData.horizontal.height
    }))
  ];
  if (items.length < 2) return;
  const setLocalData = () => {
    items.forEach((item) => {
      item.x = item.point[0] * dirn.x + item.point[1] * dirn.y;
      item.y = item.point[0] * ydir.x + item.point[1] * ydir.y;
      item.minX = item.x - item.width;
      item.maxX = item.x;
      item.minY = item.y;
      item.maxY = item.y + item.height;
      item.centerY = item.y + item.height / 2;
    });
    const rows = [];
    [...items]
      .sort((a, b) => a.centerY - b.centerY)
      .forEach((item) => {
        let row = rows.find((r) => Math.abs(r.centerY - item.centerY) <= Math.max(item.height, r.height) / 2);
        if (!row) {
          row = { centerY: item.centerY, height: item.height, count: 0 };
          rows.push(row);
        }
        row.centerY = (row.centerY * row.count + item.centerY) / (row.count + 1);
        row.height = Math.max(row.height, item.height);
        row.count += 1;
        item.row = rows.indexOf(row);
      });
  };
  const getItemBox = (item, dx = 0, dy = 0) => ({
    minX: item.minX + dx,
    maxX: item.maxX + dx,
    minY: item.minY + dy,
    maxY: item.maxY + dy
  });
  const getBoxesGap = (a, b) => ({
    x: Math.max(a.minX - b.maxX, b.minX - a.maxX, 0),
    y: Math.max(a.minY - b.maxY, b.minY - a.maxY, 0)
  });
  const areConnected = (a, b) => {
    const boxA = getItemBox(a);
    const boxB = getItemBox(b);
    const boxGap = getBoxesGap(boxA, boxB);
    return (boxGap.x <= gap + tolerance && boxGap.y <= tolerance) || (boxGap.y <= gap + tolerance && boxGap.x <= tolerance);
  };
  const getClusterBox = (cluster, dx = 0, dy = 0) =>
    cluster.reduce(
      (box, item) => {
        const itemBox = getItemBox(item, dx, dy);
        return {
          minX: Math.min(box.minX, itemBox.minX),
          maxX: Math.max(box.maxX, itemBox.maxX),
          minY: Math.min(box.minY, itemBox.minY),
          maxY: Math.max(box.maxY, itemBox.maxY)
        };
      },
      { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity }
    );
  const getClusters = () => {
    const clusters = [];
    const visited = new Set();
    items.forEach((item) => {
      if (visited.has(item)) return;
      const cluster = [];
      const queue = [item];
      visited.add(item);
      while (queue.length) {
        const current = queue.shift();
        cluster.push(current);
        items.forEach((candidate) => {
          if (visited.has(candidate) || !areConnected(current, candidate)) return;
          visited.add(candidate);
          queue.push(candidate);
        });
      }
      cluster.forEach((clusterItem) => {
        clusterItem.cluster = clusters.length;
      });
      clusters.push(cluster);
    });
    return clusters;
  };
  const getPanelRect = (item, dx = 0, dy = 0) => {
    const pxr = item.point[0] + dirn.x * dx + ydir.x * dy;
    const pyr = item.point[1] + dirn.y * dx + ydir.y * dy;
    const pxl = pxr - dirn.x * item.width;
    const pyl = pyr - dirn.y * item.width;
    const pxtr = pxr + ydir.x * item.height;
    const pytr = pyr + ydir.y * item.height;
    const pxtl = pxl + ydir.x * item.height;
    const pytl = pyl + ydir.y * item.height;
    return [
      new THREE.Vector3((pxr - cx) * scale, (pyr - cy) * scale, 0),
      new THREE.Vector3((pxtr - cx) * scale, (pytr - cy) * scale, 0),
      new THREE.Vector3((pxtl - cx) * scale, (pytl - cy) * scale, 0),
      new THREE.Vector3((pxl - cx) * scale, (pyl - cy) * scale, 0)
    ];
  };
  const distancePointToSegment = (p, a, b) => {
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const apx = p.x - a.x;
    const apy = p.y - a.y;
    const ab2 = abx * abx + aby * aby;
    const t = ab2 > 0 ? Math.max(0, Math.min(1, (apx * abx + apy * aby) / ab2)) : 0;
    const x = a.x + abx * t;
    const y = a.y + aby * t;
    return ((p.x - x) ** 2 + (p.y - y) ** 2) ** 0.5;
  };
  const getPolygonEdges = (poly) => {
    if (!Array.isArray(poly) || poly.length < 2) return [];
    return poly.map((p, pi) => [p, poly[(pi + 1) % poly.length]]);
  };
  const getRectEdgeDistance = (rect, a, b) => {
    const rectEdges = getPolygonEdges(rect);
    if (!rectEdges.length) return Infinity;
    const rectToEdge = rect.map((p) => distancePointToSegment(p, a, b));
    const edgeToRect = [a, b].flatMap((p) => rectEdges.map(([ra, rb]) => distancePointToSegment(p, ra, rb)));
    return Math.min(...rectToEdge, ...edgeToRect);
  };
  const getClearanceEdges = () =>
    [roofPolygon, ...excludedPolygons]
      .filter((poly) => Array.isArray(poly) && poly.length >= 3)
      .flatMap((poly) => getPolygonEdges(poly));
  const getClusterEdgeDistances = (cluster, dx = 0, dy = 0) => {
    const rects = cluster.map((item) => getPanelRect(item, dx, dy));
    const clearanceEdges = getClearanceEdges();
    if (!clearanceEdges.length) return [];
    return clearanceEdges.map(([a, b]) => Math.min(...rects.map((rect) => getRectEdgeDistance(rect, a, b))));
  };
  const getDistanceCost = (distance, t, L, r, p = 2) => {
    if (!Number.isFinite(distance)) return 0;
    const safeDistance = Math.max(distance, 0);
    const safeT = Math.max(t, tolerance);
    const safeL = Math.max(L, r + tolerance);
    const safeR = Math.max(r, tolerance);
    const s = safeT / (safeL / safeR - 1) ** (1 / p);
    return safeL / (1 + (safeDistance / s) ** p);
  };
  const getClearanceCost = (cluster, dx = 0, dy = 0) => {
    const targetClearance = Math.max(gap * 2, 0.05);
    return getClusterEdgeDistances(cluster, dx, dy).reduce(
      (score, distance) => score + getDistanceCost(distance, targetClearance, 1000, 1),
      0
    );
  };
  const isValidOffset = (cluster, dx, dy) => {
    const clusterSet = new Set(cluster);
    return cluster.every((item) => {
      const rect = getPanelRect(item, dx, dy);
      if (!testPoly(rect, excludedPolygons)) return false;
      const itemBox = getItemBox(item, dx, dy);
      return items.every((other) => {
        if (clusterSet.has(other)) return true;
        if (polygonsIntersect(rect, getPanelRect(other), false, 0.001)) return false;
        const boxGap = getBoxesGap(itemBox, getItemBox(other));
        return boxGap.x >= gap - tolerance || boxGap.y >= gap - tolerance;
      });
    });
  };
  const getSpacingScore = (cluster, clusters, clusterIndex, dx = 0, dy = 0) => {
    const box = getClusterBox(cluster, dx, dy);
    return clusters.reduce((score, other, otherIndex) => {
      if (otherIndex === clusterIndex) return score;
      const otherBox = getClusterBox(other);
      const boxGap = getBoxesGap(box, otherBox);
      let nextScore = score;
      if (boxGap.y <= tolerance) nextScore += Math.max(0, boxGap.x - gap);
      if (boxGap.x <= tolerance) nextScore += Math.max(0, boxGap.y - gap);
      return nextScore;
    }, 0);
  };
  const getObjectiveScore = (phase, cluster, clusters, clusterIndex, dx = 0, dy = 0, baseSpacingScore = null) => {
    const spacingScore = getSpacingScore(cluster, clusters, clusterIndex, dx, dy);
    if (phase === 'spacing') return spacingScore;
    const spacingPenalty = baseSpacingScore === null ? 0 : Math.max(0, spacingScore - baseSpacingScore - tolerance) * 100;
    return getClearanceCost(cluster, dx, dy) + spacingPenalty;
  };
  const getOffsetCandidates = (cluster, clusters, clusterIndex) => {
    const box = getClusterBox(cluster);
    const candidates = [];
    const minPanelStep = Math.min(...cluster.map((item) => Math.min(item.width, item.height)));
    const moveStep = Math.max(gap * 4, minPanelStep);
    clusters.forEach((other, otherIndex) => {
      if (otherIndex === clusterIndex) return;
      const otherBox = getClusterBox(other);
      const boxGap = getBoxesGap(box, otherBox);
      const xOverlap = boxGap.x <= tolerance;
      const yOverlap = boxGap.y <= tolerance;
      if (yOverlap && otherBox.minX >= box.maxX + gap + tolerance) candidates.push([otherBox.minX - box.maxX - gap, 0]);
      if (yOverlap && box.minX >= otherBox.maxX + gap + tolerance) candidates.push([-(box.minX - otherBox.maxX - gap), 0]);
      if (xOverlap && otherBox.minY >= box.maxY + gap + tolerance) candidates.push([0, otherBox.minY - box.maxY - gap]);
      if (xOverlap && box.minY >= otherBox.maxY + gap + tolerance) candidates.push([0, -(box.minY - otherBox.maxY - gap)]);
    });
    const clearanceSteps = [moveStep, moveStep * 0.75, moveStep * 0.5, moveStep * 0.25, gap * 2, gap].flatMap((step) => [
      [step, 0],
      [-step, 0],
      [0, step],
      [0, -step],
      [step, step],
      [step, -step],
      [-step, step],
      [-step, -step]
    ]);
    return [
      ...candidates.flatMap(([dx, dy]) => [1, 0.75, 0.5, 0.25].map((ratio) => [dx * ratio, dy * ratio])),
      ...clearanceSteps
    ];
  };
  ['spacing', 'spacing', 'clearance', 'clearance'].forEach((phase) => {
    setLocalData();
    const clusters = getClusters();
    if (!clusters.length) return;
    clusters.forEach((cluster, clusterIndex) => {
      const baseSpacingScore = getSpacingScore(cluster, clusters, clusterIndex);
      const baseScore = getObjectiveScore(phase, cluster, clusters, clusterIndex, 0, 0, baseSpacingScore);
      const best = getOffsetCandidates(cluster, clusters, clusterIndex).reduce(
        (currentBest, [dx, dy]) => {
          if (Math.abs(dx) <= tolerance && Math.abs(dy) <= tolerance) return currentBest;
          if (!isValidOffset(cluster, dx, dy)) return currentBest;
          if (
            phase === 'clearance' &&
            getSpacingScore(cluster, clusters, clusterIndex, dx, dy) > baseSpacingScore + tolerance
          ) {
            return currentBest;
          }
          const score = getObjectiveScore(phase, cluster, clusters, clusterIndex, dx, dy, baseSpacingScore);
          if (score >= currentBest.score - tolerance) return currentBest;
          return { dx, dy, score };
        },
        { dx: 0, dy: 0, score: baseScore }
      );
      if (Math.abs(best.dx) <= tolerance && Math.abs(best.dy) <= tolerance) return;
      cluster.forEach((item) => {
        item.point[0] += dirn.x * best.dx + ydir.x * best.dy;
        item.point[1] += dirn.y * best.dx + ydir.y * best.dy;
        item.x += best.dx;
        item.y += best.dy;
        item.minX += best.dx;
        item.maxX += best.dx;
        item.minY += best.dy;
        item.maxY += best.dy;
        item.centerY += best.dy;
      });
    });
  });
};

export const runPanelPlacement = (
  state,
  initData,
  getSetbacks,
  all = false,
  selectedSegments = [],
  fillMode = 3,
  layoutMode = 1
) => {
  const { orgVertices, vertices3D, verticesType, parents, eaves, pitches, azimuths, center } = state;

  const {
    segments,
    panels,
    panelType,
    scale,
    resolution,
    buffer,
    rowSpacing,
    panelTilt,
    azimuthRestriction,
    maxPanelsPerRoof,
    maxTestCellsPerRoof,
    compactGridSteps
  } = initData;

  const [cx, cy] = center;
  const c = 1.0;

  let newPanels = Array.isArray(panels) ? [...panels] : [];
  const maxPerRoof =
    Number.isFinite(Number(maxPanelsPerRoof)) && Number(maxPanelsPerRoof) > 0 ? Number(maxPanelsPerRoof) : null;
  const maxTestsPerRoof =
    Number.isFinite(Number(maxTestCellsPerRoof)) && Number(maxTestCellsPerRoof) > 0 ? Number(maxTestCellsPerRoof) : null;
  const requestedCompactGridSteps = Number(compactGridSteps);
  const safeCompactGridSteps =
    Number.isInteger(requestedCompactGridSteps) &&
    requestedCompactGridSteps >= 3 &&
    requestedCompactGridSteps <= 17 &&
    requestedCompactGridSteps % 2 === 1
      ? requestedCompactGridSteps
      : 17;
  const z = new THREE.Vector3(0, 0, 1);

  const rackBuffer = mmToSceneUnit(buffer, resolution);
  const rowSpace = mmToSceneUnit(rowSpacing * 25.4 /* inch to mm */, resolution);
  const stepx = 1.0;
  const stepy = 1.0;
  const isCompactLayout = Number(layoutMode) === 4;

  const initPanel = (i) => initializePanel(segments[i], pitches[i], panelTilt, panelType, resolution);
  const initPanels = () => segments.map((s, si) => initPanel(si));

  if (newPanels.length === 0) newPanels = initPanels();

  const roofIndices = all ? segments.map((seg, index) => index) : selectedSegments;

  const safeAzimuthRestriction =
    Array.isArray(azimuthRestriction) && azimuthRestriction.length === 2 ? azimuthRestriction : [0, 360];
  const [minAzimuth, maxAzimuth] = safeAzimuthRestriction;

  roofIndices.forEach((index) => {
    if (segments?.[index]) {
      segments[index].is_lagacy = false;
    }
    const passAzimuth = azimuths[index] >= minAzimuth && azimuths[index] <= maxAzimuth;
    if (typeof index === 'number' && passAzimuth) {
      if (!eaves[index] || !vertices3D[index] || !orgVertices[index]) return;
      const [, , , ai, bi] = eaves[index];
      if (!(ai in vertices3D[index] && bi in vertices3D[index])) return;
      const a = vertices3D[index][ai];
      const b = vertices3D[index][bi];
      const ax = orgVertices[index][ai];
      const bx = orgVertices[index][bi];
      const [dir, ydir, n] = getPlaneAxisForPanels(eaves[index], vertices3D[index]);
      if (!dir || !ydir) return;

      // const dir = b.clone().sub(a);
      // const [prepdir, updir] = getAxisVectors(dir);
      // const ydir = prepdir.normalize().clone();//new THREE.Vector3(prepdir.x, prepdir.y, 0).normalize();//
      // const ydir2 = dir.clone().cross(z).normalize();
      const dirn = dir.clone().normalize();

      const [cl, cr, cb, ct] = getSegmentRange(vertices3D, eaves, scale, index /* ,index, a, dirn, ydir */);
      const setbacks = getSetbacks(vertices3D[index], index);
      const shrinkedPoly = shrinkPolygon(vertices3D[index], setbacks).filter((_, sind) => setbacks[sind] > 0);
      const triangleIndices = THREE.ShapeUtils.triangulateShape(vertices3D[index], []);
      const includedPolygons = triangleIndices
        .map((indices) => indices.map((ti) => vertices3D[index][ti]))
        .map((ti) => (THREE.ShapeUtils.isClockWise(ti) ? ti : ti.reverse()));
      const compactObstacleBuffer = isCompactLayout ? Math.max(rackBuffer, 0.05) : 0;
      const excludedPolygons = [
        ...shrinkedPoly,
        ...getRoofObstacles(vertices3D, orgVertices, verticesType, parents, index, getSetbacks, compactObstacleBuffer)
      ];

      newPanels[index] = initPanel(index);
      const [ox, oy] = [rackBuffer, rackBuffer + rowSpace];

      const testPoint = (pnt, exPolygons) =>
        includedPolygons.some((inP) => pointInConvexPolygon(inP, pnt, -1e-5)) &&
        exPolygons.every((exP) => !pointInConvexPolygon(exP, pnt, -1e-5));
      const segmentsCross = (a1, a2, b1, b2) => {
        const det = (a2.x - a1.x) * (b2.y - b1.y) - (b2.x - b1.x) * (a2.y - a1.y);
        if (Math.abs(det) < 1e-10) return false;
        const t = ((b2.y - b1.y) * (b2.x - a1.x) + (b1.x - b2.x) * (b2.y - a1.y)) / det;
        const u = ((a1.y - a2.y) * (b2.x - a1.x) + (a2.x - a1.x) * (b2.y - a1.y)) / det;
        return t > 1e-6 && t < 1 - 1e-6 && u > 1e-6 && u < 1 - 1e-6;
      };
      const polyCrossesRoofBoundary = (poly) =>
        poly.some((p1, pi) => {
          const p2 = poly[(pi + 1) % poly.length];
          return vertices3D[index].some((r1, ri) =>
            segmentsCross(p1, p2, r1, vertices3D[index][(ri + 1) % vertices3D[index].length])
          );
        });
      const testPoly = (poly, exPolygons) => {
        let inside = poly.every((spVertex) => includedPolygons.some((inP) => pointInConvexPolygon(inP, spVertex, -1e-5)));
        if (inside) {
          inside = inside && !intersectsExcludedPolygons(poly, exPolygons);
          if (inside) {
            const [v1, v2, v3, v4] = poly;
            const v12 = v1.clone().add(v2.clone().sub(v1).multiplyScalar(0.5));
            const v23 = v2.clone().add(v3.clone().sub(v2).multiplyScalar(0.5));
            const v34 = v3.clone().add(v4.clone().sub(v3).multiplyScalar(0.5));
            const v41 = v4.clone().add(v1.clone().sub(v4).multiplyScalar(0.5));

            const vcc = v1.clone().add(v2.clone()).add(v3.clone()).add(v4.clone()).multiplyScalar(0.25);

            const v123 = v12.clone().add(v23.clone().sub(v12).multiplyScalar(0.5));
            const v234 = v23.clone().add(v34.clone().sub(v23).multiplyScalar(0.5));
            const v134 = v34.clone().add(v41.clone().sub(v34).multiplyScalar(0.5));
            const v124 = v41.clone().add(v12.clone().sub(v41).multiplyScalar(0.5));

            const polyd = [v1, v12, v2, v23, v3, v34, v4, v41, v123, v234, v134, v124, vcc];
            inside =
              inside &&
              !polyCrossesRoofBoundary(poly) &&
              polyd.every((spVertex) => isPointInPoly(spVertex, vertices3D[index], true, 0.001)) &&
              !intersectsExcludedPolygons(poly, exPolygons);
          }
        }
        return inside;
      };
      const eaveYOffset = setbacks[ai] / scale + 0.01;

      const panelVW = newPanels[index].vertical.width;
      const panelVH = newPanels[index].vertical.height;
      const panelHW = newPanels[index].horizontal.width;
      const panelHH = newPanels[index].horizontal.height;

      const gridSteps = isCompactLayout ? safeCompactGridSteps : 7;
      const gridxSteps = new Array(gridSteps).fill(0).map((_, i) => i - (gridSteps - 1) / 2);
      const gridySteps = new Array(gridSteps).fill(0).map((_, i) => i - (gridSteps - 1) / 2);
      let fill = [
        ['v', 'h'],
        ['h', 'v']
      ];
      if (fillMode === 1) fill = [['h', '']];
      else if (fillMode === 2) fill = [['v', '']];
      else if (fillMode === 3) fill = [['v', 'h']];
      else if (fillMode === 4) fill = [['h', 'v']];
      let modes = [true, false];
      if (layoutMode == 2 || isCompactLayout) modes = [false];
      else if (layoutMode == 3) modes = [true];
      const layoutModes = cartesian(fill, gridxSteps, gridySteps, modes);
      const layouts = layoutModes.map(([dr1, dr2, ofx, ofy, half]) => {
        const verticals = [];
        const horizontals = [];
        const exPolygons = [...excludedPolygons];

        let addedToSegment = 0;
        let testedCells = 0;
        let stop = false;
        [dr1, dr2].forEach((dr, fillIndex) => {
          if (dr !== 'v' && dr !== 'h') return;
          if (maxPerRoof !== null && addedToSegment >= maxPerRoof) return;
          if (stop) return;
          const vw = dr === 'v' ? panelVW : panelHW;
          const vh = dr === 'v' ? panelVH : panelHH;
          const pw = Math.min(panelVW, panelHW);
          const ph = Math.min(panelVH, panelHH);

          const xcountLeft = cl / vw;
          const xcountRight = cr / vw;
          const ycountTop = ct / vh;
          const ycountBottom = cb / vh;

          const ox0 = a.x / scale + cx / c;
          const oy0 = a.y / scale + cy / c + ofy * (ph / gridySteps.length);
          const getExistingLocalBounds = () => {
            const items = [
              ...verticals.map((point) => ({ point, width: panelVW, height: panelVH })),
              ...horizontals.map((point) => ({ point, width: panelHW, height: panelHH }))
            ];
            if (!items.length) return null;
            return items.reduce(
              (bounds, { point: [pxr, pyr], width, height }) => {
                const right = (pxr - ox0) * dirn.x + (pyr - oy0) * dirn.y;
                const bottom = (pxr - ox0) * ydir.x + (pyr - oy0) * ydir.y;
                return {
                  minX: Math.min(bounds.minX, right - width),
                  maxX: Math.max(bounds.maxX, right),
                  minY: Math.min(bounds.minY, bottom),
                  maxY: Math.max(bounds.maxY, bottom + height)
                };
              },
              {
                minX: Infinity,
                maxX: -Infinity,
                minY: Infinity,
                maxY: -Infinity
              }
            );
          };
          const getExistingLocalRows = () => {
            const source = dr1 === 'v' ? verticals : horizontals;
            const width = dr1 === 'v' ? panelVW : panelHW;
            const height = dr1 === 'v' ? panelVH : panelHH;
            const rowTolerance = Math.max(rackBuffer * 0.5, 0.001);
            return source
              .map(([pxr, pyr]) => {
                const right = (pxr - ox0) * dirn.x + (pyr - oy0) * dirn.y;
                const bottom = (pxr - ox0) * ydir.x + (pyr - oy0) * ydir.y;
                return {
                  minX: right - width,
                  maxX: right,
                  minY: bottom,
                  maxY: bottom + height
                };
              })
              .sort((a, b) => a.minY - b.minY)
              .reduce((rows, item) => {
                const row = rows.find((existingRow) => Math.abs(existingRow.minY - item.minY) <= rowTolerance);
                if (!row) return [...rows, { ...item }];
                row.minX = Math.min(row.minX, item.minX);
                row.maxX = Math.max(row.maxX, item.maxX);
                row.minY = Math.min(row.minY, item.minY);
                row.maxY = Math.max(row.maxY, item.maxY);
                return rows;
              }, []);
          };
          const placeRow = (localY, rowIndex, xOffset = 0, xMin = null, xMax = null, xScanStep = null) => {
            if (maxPerRoof !== null && addedToSegment >= maxPerRoof) return 0;
            if (stop) return 0;
            const px = ox0 + ydir.x * localY;
            const py = oy0 + ydir.y * localY;
            let x =
              -(vw + ox) * xcountLeft + (vw / 2) * (half ? rowIndex % 2 : 0) + ofx * (pw / gridxSteps.length) + xOffset;
            if (xMin !== null) x = Math.max(x, xMin);
            const rowMaxX = xMax ?? vw * xcountRight;
            let addedToRow = 0;

            while (x < rowMaxX) {
              if (maxPerRoof !== null && addedToSegment + addedToRow >= maxPerRoof) break;
              if (stop) break;
              testedCells += 1;
              if (maxTestsPerRoof !== null && testedCells >= maxTestsPerRoof) {
                stop = true;
                break;
              }
              const pxl = px + dirn.x * x;
              const pyl = py + dirn.y * x;
              const v4 = new THREE.Vector3((pxl - cx / c) * scale, (pyl - cy / c) * scale, 0);

              let test = false;
              if (testPoint(v4, exPolygons)) {
                const pxr = pxl + dirn.x * vw;
                const pyr = pyl + dirn.y * vw;
                const pxtl = pxl + ydir.x * vh;
                const pytl = pyl + ydir.y * vh;
                const pxtr = pxr + ydir.x * vh;
                const pytr = pyr + ydir.y * vh;

                const v1 = new THREE.Vector3((pxr - cx / c) * scale, (pyr - cy / c) * scale, 0);
                const v2 = new THREE.Vector3((pxtr - cx / c) * scale, (pytr - cy / c) * scale, 0);
                const v3 = new THREE.Vector3((pxtl - cx / c) * scale, (pytl - cy / c) * scale, 0);

                const solarPanel = [v1, v2, v3, v4];
                test = testPoly(solarPanel, exPolygons);

                if (test) {
                  addedToRow += 1;
                  if (dr === 'v') verticals.push([pxr, pyr]);
                  else horizontals.push([pxr, pyr]);
                  if (fillMode >= 3) exPolygons.push([v1, v2, v3, v4]);
                  // if (fillMode != 3 && exPolygons.length > 50) exPolygons.shift();
                }
              }

              const failedStep =
                xScanStep ?? (isCompactLayout ? Math.max((vw + ox) / 10, (ox || vw / 10) / 2) : stepx * (vw + ox));
              x += test ? vw + ox : failedStep;
            }
            return addedToRow;
          };
          if (fillIndex > 0 && fillMode >= 3) {
            const existingBounds = getExistingLocalBounds();
            let handledSecondPass = false;
            if (existingBounds) {
              handledSecondPass = true;
              const topLocalY = (vh + oy) * ycountTop + eaveYOffset;
              const bottomLocalY = -(vh + oy) * ycountBottom + eaveYOffset;
              const rightLocalX = vw * xcountRight;
              const leftLocalX = -(vw + ox) * xcountLeft;
              const rowScanStep = isCompactLayout
                ? Math.max((vh + oy) / 10, (oy || vh / 10) / 2)
                : Math.max(vh / 10, rackBuffer / 2, 0.001);
              const rowXOffsets = [0, rackBuffer, -rackBuffer, vw / 4, -vw / 4, vw / 2, -vw / 2].filter(
                (offset, offsetIndex, offsets) =>
                  offsets.findIndex((value) => Math.abs(value - offset) < 0.001) === offsetIndex
              );
              const placeRowWithOffsets = (localY, rowIndex, xMin = null, xMax = null, xScanStep = null) => {
                for (let offsetIndex = 0; offsetIndex < rowXOffsets.length; offsetIndex += 1) {
                  const addedToRow = placeRow(localY, rowIndex, rowXOffsets[offsetIndex], xMin, xMax, xScanStep);
                  if (addedToRow > 0) return addedToRow;
                  if (stop) return 0;
                }
                return 0;
              };
              const rowExtendScanStep = isCompactLayout
                ? Math.max((vw + ox) / 10, (ox || vw / 10) / 2)
                : Math.max(vw / 10, rackBuffer / 2, 0.001);
              const existingRows = getExistingLocalRows();
              const debugCounts = { top: 0, bottom: 0, extendRight: 0, extendLeft: 0 };
              const placeRowBandWithOffsets = (row, rowIndex, xMin, xMax) => {
                const minLocalY = row.minY;
                const maxLocalY = row.maxY - vh;
                if (maxLocalY < minLocalY) return placeRowWithOffsets(minLocalY, rowIndex, xMin, xMax, rowExtendScanStep);
                let addedToBand = 0;
                for (let localY = minLocalY; localY <= maxLocalY + 0.001; ) {
                  if (maxPerRoof !== null && addedToSegment + addedToBand >= maxPerRoof) break;
                  if (stop) break;
                  const addedToRow = placeRowWithOffsets(localY, rowIndex, xMin, xMax, rowExtendScanStep);
                  addedToBand += addedToRow;
                  localY += addedToRow > 0 ? vh + oy : rowScanStep;
                }
                return addedToBand;
              };
              let rowIndex = 0;
              for (let localY = existingBounds.maxY + rackBuffer; localY < topLocalY; ) {
                if (maxPerRoof !== null && addedToSegment >= maxPerRoof) return;
                if (stop) return;
                const addedToRow = placeRowWithOffsets(localY, rowIndex);
                debugCounts.top += addedToRow;
                addedToSegment += addedToRow;
                localY += addedToRow > 0 ? vh + oy : rowScanStep;
                if (addedToRow > 0) rowIndex += 1;
              }
              for (let localY = existingBounds.minY - rackBuffer - vh; localY + vh > bottomLocalY; ) {
                if (maxPerRoof !== null && addedToSegment >= maxPerRoof) return;
                if (stop) return;
                const addedToRow = placeRowWithOffsets(localY, rowIndex);
                debugCounts.bottom += addedToRow;
                addedToSegment += addedToRow;
                localY -= addedToRow > 0 ? vh + oy : rowScanStep;
                if (addedToRow > 0) rowIndex += 1;
              }
              existingRows.forEach((row) => {
                if (maxPerRoof !== null && addedToSegment >= maxPerRoof) return;
                if (stop) return;
                const addedRight = placeRowBandWithOffsets(row, rowIndex, row.maxX + rackBuffer, rightLocalX);
                debugCounts.extendRight += addedRight;
                addedToSegment += addedRight;
                if (addedRight > 0) rowIndex += 1;
                if (maxPerRoof !== null && addedToSegment >= maxPerRoof) return;
                if (stop) return;
                const addedLeft = placeRowBandWithOffsets(row, rowIndex, leftLocalX, row.minX - rackBuffer - vw + 0.001);
                debugCounts.extendLeft += addedLeft;
                addedToSegment += addedLeft;
                if (addedLeft > 0) rowIndex += 1;
              });
              debugPanelPlacement('second-pass summary', {
                roofIndex: index,
                dr1,
                dr,
                fillMode,
                layoutMode,
                addedToSegment,
                debugCounts,
                existingRowCount: existingRows.length,
                stop,
                testedCells,
                verticalCount: verticals.length,
                horizontalCount: horizontals.length
              });
            }
            if (handledSecondPass) return;
          }

          for (let i = -ycountBottom; i < ycountTop; i += stepy) {
            if (maxPerRoof !== null && addedToSegment >= maxPerRoof) break;
            if (stop) break;
            addedToSegment += placeRow((vh + oy) * i + eaveYOffset, i);
          }
        });

        return [addedToSegment, verticals, horizontals];
      });

      let [cenx, ceny] = segments[index].geometry.reduce((pa, pb) => [pa[0] + pb[0], pa[1] + pb[1]], [0, 0]);
      cenx /= segments[index].geometry.length;
      ceny /= segments[index].geometry.length;
      const counts = layouts.map(([cnt]) => cnt);
      const maxCount = Math.max(...counts);
      if (maxCount > 1) {
        const indices = counts.flatMap((e, i) => (e === maxCount ? i : []));
        const layoutScores = indices.map((i) => {
          const [, verticals, horizontals] = layouts[i];
          const clusterSizes = getLayoutClusterSizes(
            verticals,
            horizontals,
            panelVW,
            panelVH,
            panelHW,
            panelHH,
            dirn,
            ydir,
            rackBuffer
          );
          const [vcx, vcy] = verticals
            .map(([px, py]) => [
              px - dirn.x * (panelVW / 2) + ydir.x * (panelVH / 2),
              py - dirn.y * (panelVW / 2) + ydir.y * (panelVH / 2)
            ])
            .reduce((pa, pb) => [pa[0] + pb[0], pa[1] + pb[1]], [0, 0]);
          const [hcx, hcy] = horizontals
            .map(([px, py]) => [
              px - dirn.x * (panelHW / 2) + ydir.x * (panelHH / 2),
              py - dirn.y * (panelHW / 2) + ydir.y * (panelHH / 2)
            ])
            .reduce((pa, pb) => [pa[0] + pb[0], pa[1] + pb[1]], [0, 0]);
          const pcx = (vcx + hcx) / maxCount;
          const pcy = (vcy + hcy) / maxCount;
          return {
            index: i,
            clusterSizes,
            distToCenter: ((pcx - cenx) ** 2 + (pcy - ceny) ** 2) ** 0.5
          };
        });
        const [{ index: layoutIndex }] = layoutScores.sort((a, b) => {
          const clusterCompare = compareClusterSizes(b.clusterSizes, a.clusterSizes);
          if (clusterCompare !== 0) return clusterCompare;
          return a.distToCenter - b.distToCenter;
        });
        if (layouts[layoutIndex]) {
          const [, verticals, horizontals] = layouts[layoutIndex];

          // const [vcx, vcy] = verticals.map(([px, py]) => [px - dirn.x * (panelVW / 2) + ydir.x * (panelVH / 2), py - dirn.y * (panelVW / 2) + ydir.y * (panelVH / 2)])
          //   .reduce((pa, pb) => [pa[0] + pb[0], pa[1] + pb[1]], [0, 0]);
          // const [hcx, hcy] = horizontals.map(([px, py]) => [px - dirn.x * (panelHW / 2) + ydir.x * (panelHH / 2), py - dirn.y * (panelHW / 2) + ydir.y * (panelHH / 2)])
          //   .reduce((pa, pb) => [pa[0] + pb[0], pa[1] + pb[1]], [0, 0]);
          // const pcx = (vcx + hcx) / maxCount;
          // const pcy = (vcy + hcy) / maxCount;

          // const pMarkerGeometry = new THREE.BufferGeometry();
          // pMarkerGeometry.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0], 3));
          // const pMarkerMaterial = new THREE.PointsMaterial({ color: 'red', size: 5, sizeAttenuation: false });
          // const ppnt = new THREE.Points(pMarkerGeometry, pMarkerMaterial);
          // ppnt.position.set((pcx - center[0]) * scale, 2, (pcy - center[1]) * scale);
          // sceneRef.current.add(ppnt);

          // const sMarkerGeometry = new THREE.BufferGeometry();
          // sMarkerGeometry.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0], 3));
          // const sMarkerMaterial = new THREE.PointsMaterial({ color: 'blue', size: 5, sizeAttenuation: false });
          // const spnt = new THREE.Points(sMarkerGeometry, sMarkerMaterial);
          // spnt.position.set((cenx - center[0]) * scale, 2, (ceny - center[1]) * scale);
          // sceneRef.current.add(spnt);

          verticals.forEach((point) => newPanels[index].vertical.points.push(point));
          horizontals.forEach((point) => newPanels[index].horizontal.points.push(point));
          fineTunePanelClusters(
            newPanels[index],
            dirn,
            ydir,
            scale,
            center,
            testPoly,
            excludedPolygons,
            vertices3D[index],
            rackBuffer
          );
        }
      }
    } else if (!passAzimuth) {
      newPanels[index] = initPanel(index);
    }
  });

  return newPanels;
};

const getPanelWiringCost = (panelRecta, panelRectb, resolution) => {
  const perMeterCost = 10;
  const minDistances = panelRectb.map((v1) => Math.min(...panelRecta.map((v2) => v1.distanceTo(v2))));
  const minDistance = Math.min(...minDistances);
  const fitScore = minDistances.map((d) => (d < 5 ? 1 : 0)).reduce((a, b) => a + b, 0) ** 1.5 * (perMeterCost / 3);
  const cost = minDistance * resolution * perMeterCost;
  return [cost, fitScore];
};

const getWiringCost = (selectedPanels, candidatePanelRect, candidatePanelCenter, candidateRoofIdx, segmentCenters) => {
  if (selectedPanels.length === 0) return 0;
  const selectedPanelsInCandidateRoof = selectedPanels.filter(([ri]) => ri === candidateRoofIdx);
  if (selectedPanelsInCandidateRoof.length) {
    const costAndScores = selectedPanelsInCandidateRoof.map((panel) => {
      const [, , , , , rect] = panel;
      const [cost, fitscore] = getPanelWiringCost(rect, candidatePanelRect);
      return [cost, fitscore];
    });
    let fitScore = 0;
    let cost = 0;
    if (costAndScores.length > 1) {
      fitScore = costAndScores.map((a) => a[1]).reduce((a, b) => Math.max(a, b));
      cost = costAndScores.map((a) => a[0]).reduce((a, b) => a + b) / costAndScores.length;
    } else if (costAndScores.length) {
      [[cost, fitScore]] = costAndScores;
    }
    return cost - fitScore;
  }
  const [rcx, rcy] = segmentCenters[candidateRoofIdx];
  const [px, py] = candidatePanelCenter;
  const centerCost = ((rcx - px) ** 2 + (rcy - py) ** 2) ** 0.5;
  return centerCost / 10 + 50;
};

const sortPanelShadings = (design, panelkW, top, centers, selected = [], remaining = []) => {
  const selectedRoofs = selected.map(([ri]) => ri);
  while (remaining.length > 0 && selected.length < top) {
    remaining.sort((a, b) => {
      const [ria, , , cxa, cya, recta, psolaraccessa] = a;
      const [rib, , , cxb, cyb, rectb, psolaraccessb] = b;
      const productiona = panelkW * design[ria].acAnnual * psolaraccessa;
      const productionb = panelkW * design[rib].acAnnual * psolaraccessb;
      const wiringCosta = getWiringCost(selected, recta, [cxa, cya], ria, centers);
      const wiringCostb = getWiringCost(selected, rectb, [cxb, cyb], rib, centers);
      // if (selectedRoofs.length && !selectedRoofs.includes(ria)) wiringCosta += 100;
      // if (selectedRoofs.length && !selectedRoofs.includes(rib)) wiringCostb += 100;
      return productionb - wiringCostb - (productiona - wiringCosta);
    });
    const newSelected = remaining.shift();
    selectedRoofs.push(newSelected[0]);
    selected.push(newSelected);
  }

  return [selected, remaining];
};

export const optimalSelection = (design, panelkW, annualUsage, panelShadings) => {
  let usage = annualUsage;
  let selectedPanels = [];

  const lstPanelShadings = design.map((d, di) => panelShadings.filter(([ri]) => ri === di));
  let remaining = [...panelShadings];
  let selected = [];
  let top = 0;
  const centers = lstPanelShadings.map((psh) =>
    psh
      .map((v) => [v[3], v[4]])
      .reduce((a, b) => [a[0] + b[0], a[1] + b[1]], [0, 0])
      .map((v) => (v !== 0 ? v / psh.length : 0))
  );

  while (usage >= 0 && remaining.length > 0) {
    selectedPanels = design.reduce((acc, d) => {
      acc[d.id] = d.panels.map(() => 0);
      return acc;
    }, {});
    usage = annualUsage;
    top += 5;
    [selected, remaining] = sortPanelShadings(design, panelkW, top, centers, selected, remaining);
    for (let i = 0; i < selected.length; i += 1) {
      const [ri, , pi, , , , psolaraccess] = selected[i];
      const roof = design[ri];
      const panelProduction = panelkW * roof.acAnnual * psolaraccess;
      if (usage > 0) {
        usage -= panelProduction;
        Object.values(selectedPanels)[ri][pi] = 1;
      } else break;
    }
  }

  return selectedPanels;
};

export const getSetbackData = (vertices, verticesType, setbacks) => {
  return vertices.map((poly, pi) => {
    const isObstacle = verticesType[pi] == 'obstacle';
    const s = setbacks[pi];
    if (isObstacle) return borderPolygon(poly, Math.min(1, Math.abs(s)), Math.abs(s), s > 0);
    return shrinkPolygon(poly, s);
  });
};

export const getRoofTransformationInfo = (eave, eaveHeight, isRoof, pitch, azimuth, tilt) => {
  const [a, b, eaveTypex] = eave;
  const hasEavex = a.clone().sub(b).length() > 0;
  // const hasEavex = eave && eave[1];
  let ehx = eaveHeight;
  if (isRoof && ehx === 0 && pitch === 0) ehx = 0.5;
  const eaveDirectionx = hasEavex ? b.clone().sub(a).normalize() : new THREE.Vector3(1, 0, 0);
  // const eaveTypex = eave[2];
  // const a = eave[0];
  if (hasEavex) {
    const z = new THREE.Vector3(0, 0, 1);
    const u = b.clone().sub(a).cross(z).normalize();
    const q = new THREE.Quaternion().setFromAxisAngle(
      eaveDirectionx,
      pitch * (Math.PI / 180.0) * (false && eaveTypex === 'ridge' ? -1 : 1)
    );
    // const qroll = new THREE.Quaternion().setFromAxisAngle(
    //   u,
    //   0 * (Math.PI / 180.0)
    // );
    const mxx = new THREE.Matrix4().makeTranslation(-a.x, -a.y, -a.z).premultiply(
      // new THREE.Matrix4()
      // .makeRotationFromQuaternion(qroll)
      // .premultiply(
      new THREE.Matrix4()
        .makeRotationFromQuaternion(q)
        .premultiply(
          new THREE.Matrix4().makeTranslation(a.x, a.y, a.z).premultiply(new THREE.Matrix4().makeTranslation(0, 0, -ehx))
        )
      // )
    );

    const mxpanel = new THREE.Matrix4().makeRotationFromQuaternion(
      new THREE.Quaternion().setFromAxisAngle(eaveDirectionx, tilt * (Math.PI / 180.0))
    );

    return [hasEavex, eaveTypex, eaveDirectionx, mxx, null, ehx, mxpanel];
  }

  const mxx = new THREE.Matrix4().makeTranslation(0, 0, -ehx);
  return [hasEavex, eaveTypex, eaveDirectionx, mxx, null, ehx, null];
  // if (eaveTypex === 'ridge') {
  //   const v3 = vertices.map((v) => -v.clone().applyMatrix4(mxx).z);
  //   const mh = ehx - Math.min(...v3);
  //   const mxxr = new THREE.Matrix4().makeTranslation(0, 0, -mh);
  //   return [hasEavex, eaveTypex, eaveDirectionx, mxx, mxxr, ehx];
  // }
};
